import { Box, Card, Typography } from '@mui/material';
import dayjs from 'dayjs';
import React from 'react';
import { CartesianGrid, Line, LineChart, ResponsiveContainer, Tooltip, TooltipProps, XAxis, YAxis } from 'recharts';

import palette from '../../../ColorPalette';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const CustomTick: React.FC<any> = (props: any) => {
    const { x, y, payload } = props;
    const date = dayjs(payload.value);
    return (
        <g transform={`translate(${x},${y})`}>
            <text x={0} y={0} dy={16} fill="#666">
                <tspan textAnchor="middle" x="0" fontSize={12}>
                    {date.format('MMM')}
                </tspan>
                {payload.index === 0 || date.month() === 0 ? (
                    <tspan textAnchor="middle" x="0" dy="14" fontSize={12} style={{ fontWeight: 'bold' }}>
                        {date.format('YYYY')}
                    </tspan>
                ) : null}
            </text>
        </g>
    );
};

const CustomTooltip: React.FC<TooltipProps<number, string>> = (props) => {
    const { payload, label } = props;
    if (payload && payload.length) {
        const { value } = payload[0];
        return (
            <Card
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    p: 1,
                    backgroundColor: palette.white,
                    boxShadow: 5,
                }}
            >
                <Typography variant="overline">{dayjs(label).format('MMM YYYY')}</Typography>
                <Typography variant="h6">{value}</Typography>
            </Card>
        );
    }
    return null;
};

interface CsaScoreHistoryCardProps {
    type: string;
    data: { score: number; date: string }[];
}

const CsaScoreHistoryCard: React.FC<CsaScoreHistoryCardProps> = (props: CsaScoreHistoryCardProps) => {
    return (
        <Card
            id={`${props.type.replaceAll('_', '-')}-history-card`}
            data-testid={`${props.type.replaceAll('_', '-')}-history-card`}
            sx={{
                width: '100%',
                minWidth: '500px',
                height: '300px',
                ml: 2,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'flex-start',
                p: 2,
            }}
        >
            <Typography variant="overline" sx={{ lineHeight: 1, mb: 2 }}>
                Score History
            </Typography>
            {props.data.length > 2 ? (
                <ResponsiveContainer width="100%" height="100%" className="chart-container">
                    <LineChart
                        id="score-history-chart"
                        margin={{ top: 5, right: 15, bottom: 15, left: -30 }}
                        data={props.data}
                    >
                        <CartesianGrid />
                        <XAxis
                            tick={<CustomTick />}
                            dataKey="date"
                            tickFormatter={(d) => {
                                const date = dayjs(d);
                                if (date.month() === 0) {
                                    return date.format('MMM YYYY');
                                }
                                return date.format('MMM');
                            }}
                            interval={0}
                        />
                        <YAxis
                            tick={{ fontSize: 12 }}
                            domain={[0, 100]}
                            ticks={[...Array.from({ length: 5 }, (_, i) => i * 25)]}
                            interval={0}
                        />
                        <Line
                            id="score-line"
                            dataKey="score"
                            stroke={palette.neutral[400]}
                            strokeWidth={2}
                            isAnimationActive={true}
                            animationDuration={500}
                        />
                        <Tooltip content={<CustomTooltip />} />
                    </LineChart>
                </ResponsiveContainer>
            ) : (
                <Box
                    sx={{
                        width: '100%',
                        height: '100%',
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                    }}
                >
                    <Typography variant="overline">{"We currently don't have historical data to display"}</Typography>
                </Box>
            )}
        </Card>
    );
};

export default CsaScoreHistoryCard;
